import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, CheckCircle2, Clock, MessagesSquare, ClipboardCheck, Compass, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const EMPTY = { name: "", business: "", phone: "" };

export default function Booking() {
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        if (!form.name.trim() || !form.business.trim() || !/^[+]?[0-9\s\-()]{8,16}$/.test(form.phone.trim())) {
            toast.error("Please complete your name, business name and a valid phone number.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, form);
            setDone(true);
            toast.success(
                data.email_sent === false
                    ? "Booking request saved — our team will call you to schedule your free session."
                    : "Booking request sent! Our team will call you to schedule your free 30-minute session."
            );
            setForm(EMPTY);
        } catch (err) {
            console.error(err);
            toast.error("Couldn't submit the booking — please try again or WhatsApp us.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section id="book" className="py-20 lg:py-28 bg-cream-deep/40" data-testid="booking-section">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal className="text-center max-w-2xl mx-auto">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <span className="h-px w-10 bg-gold-deep" />
                        <span className="text-[11px] font-semibold tracking-[0.34em] text-burgundy">FREE CONSULTATION</span>
                        <span className="h-px w-10 bg-gold-deep" />
                    </div>
                    <h2 className="text-4xl sm:text-5xl text-charcoal">Book Your 30-Minute Assessment</h2>
                </Reveal>

                <Reveal delay={0.08} className="mt-12">
                    <h3 className="text-center text-2xl sm:text-3xl text-charcoal">What Happens Next?</h3>
                </Reveal>
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.title} delay={i * 0.08} className="h-full">
                            <div
                                className="card-lift h-full bg-cream-soft border border-burgundy/10 rounded-2xl p-7"
                                data-testid={`next-step-${i}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="w-11 h-11 rounded-full bg-burgundy text-cream flex items-center justify-center">
                                        <s.icon className="w-5 h-5" strokeWidth={1.7} />
                                    </span>
                                    <span className="font-serif italic text-3xl text-burgundy/15 select-none">{s.n}</span>
                                </div>
                                <h4 className="mt-5 font-semibold text-[17px] text-charcoal">{s.title}</h4>
                                <p className="mt-2 text-sm text-charcoal/65 leading-relaxed">{s.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.1} className="mt-14">
                    <div
                        className="relative overflow-hidden rounded-3xl bg-burgundy px-6 sm:px-14 py-14 lg:py-16 text-center"
                        data-testid="book-call-banner"
                    >
                        <span className="pointer-events-none absolute -left-20 -top-24 w-72 h-72 rounded-full border-[22px] border-olive/20" aria-hidden="true" />
                        <span className="pointer-events-none absolute -right-16 -bottom-24 w-64 h-64 rounded-full border-[20px] border-gold/20" aria-hidden="true" />
                        <p className="relative font-serif italic text-xl text-gold">Let's Have a Conversation</p>
                        <h3 className="relative mt-3 text-3xl sm:text-4xl lg:text-5xl text-cream">Book Your Call</h3>
                        <p className="relative mt-5 text-cream/75 max-w-2xl mx-auto">
                            Secure your FREE 30-minute consultation with our team. Let's discuss your business goals,
                            understand your requirements, and explore how we can help your business grow.
                        </p>

                        {done ? (
                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="relative mt-9 max-w-md mx-auto bg-cream/10 border border-gold/40 rounded-2xl p-7"
                                data-testid="booking-success"
                            >
                                <CheckCircle2 className="w-12 h-12 text-gold mx-auto" strokeWidth={1.6} />
                                <p className="mt-4 font-serif text-2xl text-cream">Booking request received!</p>
                                <p className="mt-2 text-sm text-cream/75">
                                    Our team will call you shortly to schedule your free 30-minute session.
                                </p>
                                <button onClick={() => setDone(false)} className="btn btn-gold mt-6 !py-2.5 !px-5 text-xs" data-testid="booking-again">
                                    Book Another Call
                                </button>
                            </motion.div>
                        ) : (
                            <form onSubmit={submit} className="relative mt-9 max-w-2xl mx-auto grid sm:grid-cols-3 gap-4 text-left" data-testid="booking-form">
                                <div>
                                    <label className="field-label !text-cream/70" htmlFor="bk-name">Full Name *</label>
                                    <input id="bk-name" className="field !bg-cream/10 !border-cream/30 !text-cream placeholder:!text-cream/40" placeholder="Your full name" value={form.name} onChange={set("name")} required data-testid="booking-name" />
                                </div>
                                <div>
                                    <label className="field-label !text-cream/70" htmlFor="bk-business">Business Name *</label>
                                    <input id="bk-business" className="field !bg-cream/10 !border-cream/30 !text-cream placeholder:!text-cream/40" placeholder="Your business" value={form.business} onChange={set("business")} required data-testid="booking-business" />
                                </div>
                                <div>
                                    <label className="field-label !text-cream/70" htmlFor="bk-phone">Phone Number *</label>
                                    <input id="bk-phone" type="tel" className="field !bg-cream/10 !border-cream/30 !text-cream placeholder:!text-cream/40" placeholder="+91 ..." value={form.phone} onChange={set("phone")} required data-testid="booking-phone" />
                                </div>
                                <div className="sm:col-span-3">
                                    <button type="submit" className="btn btn-gold w-full justify-center" disabled={busy} data-testid="booking-submit">
                                        <Calendar className="w-4 h-4" />
                                        {busy ? "Sending..." : "Book Your Call"}
                                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                                    </button>
                                    <p className="mt-3 text-center text-[11px] text-cream/55">
                                        We only use these details to schedule your call — nothing else.
                                    </p>
                                </div>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

const STEPS = [
    { icon: Clock, n: "01", title: "We Respond Within 24 Hours", desc: "Once you reach out, we will respond within 24 hours." },
    { icon: MessagesSquare, n: "02", title: "We Understand Your Needs", desc: "We have a focused discussion to understand your business goals, challenges and requirements." },
    { icon: ClipboardCheck, n: "03", title: "We Assess Before Recommending", desc: "We carefully assess your needs and provide feedback before recommending any solution." },
    { icon: Compass, n: "04", title: "We Guide You Forward", desc: "We guide you towards the right next step to help your business grow." },
];

